import React, { useCallback, useEffect, useState } from 'react';
import { IPublicClientApplication } from '@azure/msal-browser';
import {
  WorkCalendar, CalendarException, WEEKDAYS, ExceptionType, MON_FRI_BITMASK, FALLBACK_HOURS_PER_DAY,
  listCalendars, createCalendar, updateCalendar, deleteCalendar, setDefaultCalendar,
  listExceptions, createException, deleteException,
} from '../../services/calendarService';

const EXC_LABEL: Record<number, string> = { [ExceptionType.Holiday]: 'Holiday', [ExceptionType.NonWorking]: 'Non-working', [ExceptionType.ExtraWorking]: 'Extra working' };

function workDaysLabel(mask: number | null): string {
  const m = mask ?? 0;
  const on = WEEKDAYS.filter(w => (m >> w.day) & 1).map(w => w.short);
  return on.length ? on.join(', ') : 'None';
}

/** Tenant Admin → Work Calendars. Create/edit calendars (work-days bitmask +
 *  hours/day), manage holiday/exception dates, and set the single tenant default
 *  (atomic — see setDefaultCalendar). */
export default function WorkCalendarsAdminCard({ pca }: { pca: IPublicClientApplication }) {
  const [cals, setCals] = useState<WorkCalendar[] | null>(null);
  const [editing, setEditing] = useState<WorkCalendar | 'new' | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try { setCals(await listCalendars(pca)); }
    catch (e) { setError(e instanceof Error ? e.message : 'Failed to load calendars.'); setCals([]); }
  }, [pca]);
  useEffect(() => { load(); }, [load]);

  const makeDefault = async (id: string) => {
    if (busy) return;
    setBusy(true); setError(null);
    try { await setDefaultCalendar(pca, id); await load(); }
    catch (e) { setError(e instanceof Error ? e.message : 'Failed to set default.'); }
    finally { setBusy(false); }
  };

  const remove = async (c: WorkCalendar) => {
    if (busy) return;
    setBusy(true);
    try { await deleteCalendar(pca, c.dagrun_pm_calendarid); await load(); }
    catch (e) { setError(e instanceof Error ? e.message : 'Failed to delete.'); }
    finally { setBusy(false); }
  };

  return (
    <div className="card" style={{ padding: 24, marginBottom: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
        <h3 style={{ margin: 0 }}>Work Calendars</h3>
        <button className="btn-primary" onClick={() => setEditing('new')} disabled={busy}>New Calendar</button>
      </div>
      <p style={{ fontSize: 13, color: 'var(--ink-3)', margin: '0 0 14px', lineHeight: 1.5 }}>
        Working days and hours/day used for capacity across the app. Exactly one calendar is the
        tenant default; projects can override it per worker. If none is default, the app falls back
        to Mon–Fri, {FALLBACK_HOURS_PER_DAY}h/day.
      </p>
      {error && <div style={{ fontSize: 12, color: '#DC2626', marginBottom: 10 }}>{error}</div>}

      {cals === null ? (
        <div style={{ fontSize: 12, color: 'var(--ink-4)' }}>Loading…</div>
      ) : cals.length === 0 ? (
        <div style={{ fontSize: 13, color: 'var(--ink-3)' }}>No calendars yet. Create one to set a tenant default.</div>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead><tr style={{ textAlign: 'left', color: 'var(--ink-4)', fontSize: 11, textTransform: 'uppercase' }}>
            <th style={{ padding: '6px 8px' }}>Name</th><th style={{ padding: '6px 8px' }}>Work days</th>
            <th style={{ padding: '6px 8px' }}>Hours/day</th><th style={{ padding: '6px 8px' }}>Default</th><th />
          </tr></thead>
          <tbody>
            {cals.map(c => (
              <tr key={c.dagrun_pm_calendarid} style={{ borderTop: '0.5px solid var(--border-light)' }}>
                <td style={{ padding: '8px', color: 'var(--ink)' }}>{c.dagrun_pm_name}</td>
                <td style={{ padding: '8px', color: 'var(--ink-2)' }}>{workDaysLabel(c.dagrun_pm_workdaysbitmask)}</td>
                <td style={{ padding: '8px', color: 'var(--ink-2)' }}>{c.dagrun_pm_hoursperday ?? '—'}</td>
                <td style={{ padding: '8px' }}>
                  {c.dagrun_pm_isdefault
                    ? <span className="pill green">Default</span>
                    : <button className="btn-secondary" style={{ fontSize: 12, padding: '3px 8px' }} onClick={() => makeDefault(c.dagrun_pm_calendarid)} disabled={busy}>Make default</button>}
                </td>
                <td style={{ padding: '8px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                  <button className="btn-secondary" style={{ fontSize: 12, padding: '3px 8px', marginRight: 6 }} onClick={() => setEditing(c)} disabled={busy}>Edit</button>
                  <button type="button" title="Delete calendar" onClick={() => remove(c)} disabled={busy}
                    style={{ background: 'transparent', border: 'none', color: 'var(--ink-4)', cursor: 'pointer', fontSize: 15 }}>×</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {editing && (
        <CalendarEditor pca={pca} initial={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />
      )}
    </div>
  );
}

function CalendarEditor({ pca, initial, onClose, onSaved }: { pca: IPublicClientApplication; initial: WorkCalendar | null; onClose: () => void; onSaved: () => void }) {
  const [name, setName] = useState(initial?.dagrun_pm_name ?? '');
  const [mask, setMask] = useState(initial?.dagrun_pm_workdaysbitmask ?? MON_FRI_BITMASK);
  const [hours, setHours] = useState(String(initial?.dagrun_pm_hoursperday ?? FALLBACK_HOURS_PER_DAY));
  const [notes, setNotes] = useState(initial?.dagrun_pm_notes ?? '');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const toggleDay = (d: number) => setMask(m => (m & (1 << d)) ? m & ~(1 << d) : m | (1 << d));

  const save = async () => {
    if (!name.trim()) { setErr('Name is required.'); return; }
    const hpd = parseFloat(hours);
    if (!isFinite(hpd) || hpd <= 0 || hpd > 24) { setErr('Hours/day must be between 0 and 24.'); return; }
    setSaving(true); setErr(null);
    try {
      if (initial) await updateCalendar(pca, initial.dagrun_pm_calendarid, { name: name.trim(), workDaysBitmask: mask, hoursPerDay: hpd, notes: notes || null });
      else await createCalendar(pca, { name: name.trim(), workDaysBitmask: mask, hoursPerDay: hpd, notes: notes || null });
      onSaved();
    } catch (e) { setErr(e instanceof Error ? e.message : 'Save failed.'); setSaving(false); }
  };

  return (
    <div style={overlay} onMouseDown={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={modal}>
        <h3 style={{ marginTop: 0 }}>{initial ? 'Edit Calendar' : 'New Calendar'}</h3>
        <label style={lbl}>Name</label>
        <input value={name} onChange={e => setName(e.target.value)} style={inp} placeholder="e.g. Standard (Mon–Fri)" />
        <label style={lbl}>Work days</label>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 12 }}>
          {WEEKDAYS.map(w => {
            const on = (mask >> w.day) & 1;
            return (
              <button key={w.day} type="button" onClick={() => toggleDay(w.day)}
                style={{ padding: '5px 10px', borderRadius: 'var(--r-sm)', fontSize: 12, cursor: 'pointer',
                  border: on ? '1px solid var(--brand)' : '1px solid var(--border)',
                  background: on ? 'var(--brand-mist)' : 'var(--surface)', color: on ? 'var(--brand)' : 'var(--ink-3)' }}>
                {w.short}
              </button>
            );
          })}
        </div>
        <label style={lbl}>Hours per working day</label>
        <input type="number" min={0} max={24} step={0.5} value={hours} onChange={e => setHours(e.target.value)} style={inp} />
        <label style={lbl}>Notes (optional)</label>
        <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2} style={{ ...inp, resize: 'vertical' }} />

        {initial && <ExceptionsEditor pca={pca} calendarId={initial.dagrun_pm_calendarid} />}
        {!initial && <div style={{ fontSize: 11, color: 'var(--ink-4)', marginTop: 4 }}>Save the calendar first, then re-open it to add holidays/exceptions.</div>}

        {err && <div style={{ fontSize: 12, color: '#DC2626', marginTop: 8 }}>{err}</div>}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
          <button className="btn-secondary" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="btn-primary" onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
        </div>
      </div>
    </div>
  );
}

function ExceptionsEditor({ pca, calendarId }: { pca: IPublicClientApplication; calendarId: string }) {
  const [excs, setExcs] = useState<CalendarException[]>([]);
  const [date, setDate] = useState('');
  const [type, setType] = useState<number>(ExceptionType.Holiday);
  const [desc, setDesc] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => { try { setExcs(await listExceptions(pca, calendarId)); } catch { /* ignore */ } }, [pca, calendarId]);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!date || busy) return;
    setBusy(true);
    try { await createException(pca, { calendarId, date, type, description: desc || null }); setDate(''); setDesc(''); await load(); }
    catch { /* ignore */ } finally { setBusy(false); }
  };
  const remove = async (id: string) => { setBusy(true); try { await deleteException(pca, id); await load(); } catch { /* ignore */ } finally { setBusy(false); } };

  return (
    <div style={{ marginTop: 14, borderTop: '0.5px solid var(--border-light)', paddingTop: 12 }}>
      <div style={{ ...lbl, marginBottom: 8 }}>Holidays / Exceptions</div>
      {excs.length > 0 && (
        <div style={{ marginBottom: 10 }}>
          {excs.map(e => (
            <div key={e.dagrun_pm_calendarexceptionid} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, padding: '3px 0' }}>
              <span style={{ fontFamily: 'var(--font-mono)', color: 'var(--ink-2)' }}>{e.dagrun_pm_exceptiondate?.slice(0, 10)}</span>
              <span style={{ color: 'var(--ink-3)' }}>{EXC_LABEL[e.dagrun_pm_exceptiontype ?? -1] ?? '?'}</span>
              {e.dagrun_pm_description && <span style={{ color: 'var(--ink-4)' }}>— {e.dagrun_pm_description}</span>}
              <button type="button" onClick={() => remove(e.dagrun_pm_calendarexceptionid)} disabled={busy}
                style={{ marginLeft: 'auto', background: 'transparent', border: 'none', color: 'var(--ink-4)', cursor: 'pointer' }}>×</button>
            </div>
          ))}
        </div>
      )}
      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
        <input type="date" value={date} onChange={e => setDate(e.target.value)} style={{ ...inp, marginBottom: 0, flex: '0 0 140px' }} />
        <select value={type} onChange={e => setType(Number(e.target.value))} style={{ ...inp, marginBottom: 0, flex: '0 0 130px' }}>
          <option value={ExceptionType.Holiday}>Holiday</option>
          <option value={ExceptionType.NonWorking}>Non-working</option>
          <option value={ExceptionType.ExtraWorking}>Extra working</option>
        </select>
        <input value={desc} onChange={e => setDesc(e.target.value)} placeholder="Description (optional)" style={{ ...inp, marginBottom: 0, flex: 1 }} />
        <button className="btn-secondary" onClick={add} disabled={!date || busy}>Add</button>
      </div>
    </div>
  );
}

const overlay: React.CSSProperties = { position: 'fixed', inset: 0, background: 'rgba(15,17,23,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 250 };
const modal: React.CSSProperties = { background: 'var(--surface)', borderRadius: 'var(--r-lg)', padding: 24, width: 560, maxWidth: '92vw', maxHeight: '88vh', overflowY: 'auto', boxShadow: 'var(--shadow-panel)' };
const lbl: React.CSSProperties = { display: 'block', fontSize: 12, color: 'var(--ink-3)', margin: '10px 0 4px', fontWeight: 600 };
const inp: React.CSSProperties = { width: '100%', padding: '8px 10px', fontSize: 13, border: '1px solid var(--border)', borderRadius: 'var(--r-md)', background: 'var(--surface)', color: 'var(--ink)', boxSizing: 'border-box', marginBottom: 4 };
